import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { Layout } from './components/Layout';
import { Loading } from './components/states';
import { AddressPoolsPage } from './pages/AddressPools';
import { AdministrationLayout } from './pages/admin/AdministrationLayout';
import { ProfileSettings } from './pages/admin/ProfileSettings';
import { SecuritySettings } from './pages/admin/SecuritySettings';
import { PlatformSettings } from './pages/admin/PlatformSettings';
import { TokensSettings } from './pages/admin/TokensSettings';
import { DashboardPage } from './pages/Dashboard';
import { DeploymentDetailPage } from './pages/DeploymentDetail';
import { DeploymentsPage } from './pages/Deployments';
import { DeviceDetailPage } from './pages/DeviceDetail';
import { DriftPage } from './pages/Drift';
import { EnrollmentTokensPage } from './pages/EnrollmentTokens';
import { ConflictsPage } from './pages/Conflicts';
import { RegistrationPage } from './pages/Registration';
import { RelaysPage } from './pages/Relays';
import { LoginPage } from './pages/Login';
import { RevisionsPage } from './pages/Revisions';
import { RouteExplorerPage } from './pages/RouteExplorer';
import { AlertRulesPage } from './pages/AlertRules';
import { DiagnosticsPage } from './pages/Diagnostics';
import { FederationPage } from './pages/Federation';
import { ReportsPage } from './pages/Reports';
import { TopologyPage } from './pages/Topology';
import { AlertsSection, DeploymentsSection, DevicesSection } from './navigation/sections';
import { OperationsOverview } from './pages/OperationsOverview';
import { NetworkOverview } from './pages/NetworkOverview';
import { RoutingOverview } from './pages/RoutingOverview';
import { WireGuardPage } from './pages/WireGuard';
import { WireGuardDetailPage } from './pages/WireGuardDetail';
import {
  AlertsPage,
  AuditPage,
  DevicesPage,
  FabricsPage,
  OrganizationsPage,
  SitesPage,
} from './pages/resources';

function Protected({ children }: { children: ReactNode }): JSX.Element {
  const { me, loading } = useAuth();
  if (loading) return <Loading />;
  if (!me) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="organizations" element={<OrganizationsPage />} />
        <Route path="sites" element={<SitesPage />} />
        {/* Edge Devices hosts onboarding as tabs (Phase 2.2); detail is its own route. */}
        <Route path="devices" element={<DevicesSection />}>
          <Route index element={<DevicesPage />} />
          <Route path="enrollment" element={<EnrollmentTokensPage />} />
          <Route path="pending" element={<RegistrationPage />} />
          <Route path="conflicts" element={<ConflictsPage />} />
        </Route>
        <Route path="devices/:id" element={<DeviceDetailPage />} />
        {/* Former top-level routes now redirect to their tab home. */}
        <Route path="enrollment" element={<Navigate to="/devices/enrollment" replace />} />
        <Route path="registration" element={<Navigate to="/devices/pending" replace />} />
        <Route path="conflicts" element={<Navigate to="/devices/conflicts" replace />} />
        <Route path="fabrics" element={<FabricsPage />} />
        <Route path="wireguard" element={<WireGuardPage />} />
        <Route path="wireguard/:id" element={<WireGuardDetailPage />} />
        <Route path="address-pools" element={<AddressPoolsPage />} />
        <Route path="route-explorer" element={<RouteExplorerPage />} />
        <Route path="topology" element={<TopologyPage />} />
        <Route path="relays" element={<RelaysPage />} />
        <Route path="revisions" element={<Navigate to="/deployments/revisions" replace />} />
        <Route path="deployments" element={<DeploymentsSection />}>
          <Route index element={<DeploymentsPage />} />
          <Route path="revisions" element={<RevisionsPage />} />
        </Route>
        <Route path="deployments/:id" element={<DeploymentDetailPage />} />
        <Route path="drift" element={<DriftPage />} />
        <Route path="network" element={<NetworkOverview />} />
        <Route path="routing" element={<RoutingOverview />} />
        <Route path="operations" element={<OperationsOverview />} />
        <Route path="alerts" element={<AlertsSection />}>
          <Route index element={<AlertsPage />} />
          <Route path="rules" element={<AlertRulesPage />} />
        </Route>
        <Route path="alert-rules" element={<Navigate to="/alerts/rules" replace />} />
        <Route path="diagnostics" element={<DiagnosticsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="federation" element={<FederationPage />} />
        <Route path="audit" element={<AuditPage />} />
        {/* Administration is a settings area with its own secondary nav (Phase 2.4). */}
        <Route path="admin" element={<AdministrationLayout />}>
          <Route index element={<ProfileSettings />} />
          <Route path="security" element={<SecuritySettings />} />
          <Route path="operations" element={<PlatformSettings />} />
          <Route path="tokens" element={<TokensSettings />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
